<script lang="ts" setup>
import { cloneDeep } from 'lodash-es';
import { Multiselect } from 'vue-multiselect';
import PermissionsService, { PermissionStateEnum, type PermissionsGroup, type PersonasGroup, type ResourceOwner } from '../../runtime/services/PermissionsService';

interface PermissionsModel
{
    text: string;
    ownerId: number;
    ownerType: string;
    resourceId: number;
    resourceType: string;
    permissionsType: string;
    permissions: Record<string, PermissionStateEnum>;
}

const { $t } = useLocalization();
const { $alert } = useAlerts();
const { $log } = useLogging();

const props = defineProps<{
    resource: string;
    id: number;
    disabled?: boolean;
}>();

const modal = ref(null);

const loading = ref(false);
const options = ref<PersonasGroup[]>([]);
const owner = ref<KeyValue<number, string>>(null);
const ownerType = ref<string>(null);
const group = ref<PermissionsGroup>(null);
const owners = ref<ResourceOwner[]>([]);

const form = ref<FormType<PermissionsModel>>(Form.create<PermissionsModel>({
    text: '',
    ownerId: 0,
    ownerType: '',
    resourceId: 0,
    resourceType: '',
    permissionsType: '',
    permissions: {}
}));

const permissionsState = computed(() => PermissionStateEnum);

const loadPermissions = async (): Promise<void> =>
{
    try
    {
        if (group.value == null)
        {
            group.value = await PermissionsService.resourcePermissions(props.resource);
        }
    }
    catch
    {
        owners.value = [];
    }
};

const loadData = async (): Promise<void> =>
{
    try
    {
        owners.value = await PermissionsService.resourceOwners(props.resource, props.id);
    }
    catch
    {
        owners.value = [];
    }
};

watch(() => props.id, async (id: number): Promise<void> =>
{
    if (id > 0 && !props.disabled)
    {
        await loadPermissions();
        await loadData();
    }
}, { immediate: true });

const searchOptions = async (query: string): Promise<void> =>
{
    options.value = await PermissionsService.getIdentities(query, true, true, true, false);
};

const onSearchChanged = useDebounceFn(async (query: string): Promise<void> =>
{
    if (query)
    {
        loading.value = true;
        searchOptions(query);
        loading.value = false;
    }
}, 500);

const onChange = (value: KeyValue<number, string>): void =>
{
    ownerType.value = options.value.find(p => p.values.includes(value))?.type;
};

const typeName = (type: string): string =>
{
    if (type.endsWith('User')) return $t('[[[Użytkownik]]]');
    else if (type.endsWith('Role')) return $t('[[[Rola]]]');
    else if (type.endsWith('Team')) return $t('[[[Zespół]]]');
    else return 'n/a';
};
const typeIcon = (owner: ResourceOwner): string =>
{
    if (owner.ownerType.endsWith('User')) return 'fa-user text-primary';
    else if (owner.ownerType.endsWith('Role')) return 'fa-users text-success';
    else if (owner.ownerType.endsWith('Team')) return 'fa-user-friends text-info';
    else return '';
};

const permissionName = (permissions: string): string =>
{
    return group.value.sections.flat().find(p => p.key == permissions)?.value.alt ?? permissions;
};

const openModal = (ownerType: string, ownerId: number, text: string, permissions: Record<string, PermissionStateEnum> = {}): void =>
{
    form.value.text = text;
    form.value.ownerId = ownerId;
    form.value.ownerType = ownerType;
    form.value.resourceId = props.id;
    form.value.resourceType = props.resource;
    form.value.permissionsType = group.value.key;
    form.value.permissions = cloneDeep(permissions);

    modal.value.show();
};

const editPermissions = (owner: ResourceOwner): void =>
{
    openModal(owner.ownerType, owner.ownerId, owner.ownerName, owner.permissions);
};

const check = (value: string, mode: PermissionStateEnum): void =>
{
    if (mode == null && form.value.permissions.hasOwnProperty(value))
    {
        delete form.value.permissions[value];
    }

    if (mode != null)
    {
        form.value.permissions = {
            ...form.value.permissions,
            [value]: mode
        };
    }
};

const checked = (value: string, mode: PermissionStateEnum): boolean =>
{
    return mode != null
        ? form.value.permissions.hasOwnProperty(value) === true && form.value.permissions[value] == mode
        : form.value.permissions.hasOwnProperty(value) === false;
};

const savePermissions = async (): Promise<void> =>
{
    try
    {
        form.value.wait();

        const keys = group.value.sections.selectMany(p => p).select(p => p.key).toArray();

        for (const key of Object.keys(form.value.permissions))
        {
            if (!keys.includes(key))
            {
                delete form.value.permissions[key];
            }
        }

        const data = form.value.only(['ownerId', 'ownerType', 'permissionsType', 'permissions']);

        await PermissionsService.saveResourcePermissions(props.resource, props.id, data);
        await loadData();
    }
    catch (ex: any)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) => form.value.$errors.record(ex.data.errors)
        });
    }
    finally
    {
        form.value.continue();
    }
};
</script>

<template>
    <div v-if="disabled" class="alert alert-warning text-center">
        {{ $t('[[[Nie możesz modyfikować uprawnień]]]') }}
    </div>
    <div v-else>
        <div class="mb-2">
            <Multiselect
                v-model="owner"
                :options="options"
                :loading="loading"
                label="value"
                :placeholder="$t('[[[wybierz...]]]')"
                group-label="group"
                group-values="values"
                :group-select="false"
                :select-label="' '"
                :selected-label="' '"
                :deselect-label="' '"
                :multiple="false"
                :searchable="true"
                :max-height="300"
                :internal-search="false"
                :clear-on-select="true"
                :close-on-select="true"
                :show-no-results="true"
                :hide-selected="false"
                :disabled="id == 0"
                @search-change="onSearchChanged"
                @update:model-value="onChange"
            >
                <template #noOptions>
                    {{ $t('[[[Lista jest pusta]]]') }}
                </template>
                <template #noResult>
                    {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
                </template>
            </Multiselect>
        </div>
        <div class="text-end mb-2">
            <IdeoButton variant="success" :disabled="!owner" @click="openModal(ownerType, owner.key, owner.value)">
                {{ $t('[[[Dodaj]]]') }}
            </IdeoButton>
        </div>
        <div class="list-group">
            <div
                v-for="item in owners"
                :key="`${item.ownerType}.${item.ownerId}`"
                class="list-group-item list-group-item-action p-2 d-flex pointer"
                @click="editPermissions(item)"
            >
                <div class="d-flex align-items-start pe-2 me-2 border-end">
                    <i class="fad fa-fw fa-2x" :class="typeIcon(item)" :title="typeName(item.ownerType)" />
                </div>
                <div class="flex-fill ps-1">
                    <div class="d-flex align-items-center">
                        <div class="flex-fill fw-medium">
                            {{ item.ownerName }}
                        </div>
                        <IdeoButton variant="light" size="sm" pill :title="$t('[[[Edytuj uprawnienia]]]')" @click.stop.prevent="editPermissions(item)">
                            <i class="far fa-pencil" />
                        </IdeoButton>
                    </div>
                    <div class="mt-1">
                        <div v-for="([permission, status], j) in Object.entries(item.permissions)" :key="j" class="d-flex align-items-center small">
                            <i v-if="status == permissionsState.Allow" class="fas fa-fw fa-check text-success" :title="$t('[[[Dostęp przyznany]]]')" />
                            <i v-if="status == permissionsState.Deny" class="fas fa-fw fa-times text-danger" :title="$t('[[[Dostęp zabroniony]]]')" />
                            <span class="ms-1">{{ permissionName(permission) }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="owners.length == 0" class="list-group-item list-group-item-light text-center">
                {{ $t('[[[Nikomu nie przydzielono uprawnień]]]') }}
            </div>
        </div>

        <!-- Modal -->
        <IdeoModal ref="modal" title="ad">
            <template #modal-header>
                <h5 class="modal-title">
                    {{ form.text }} <small class="text-muted">{{ typeName(form.ownerType) }}</small>
                </h5>
            </template>
            <div v-for="(section, i) in group.sections" :key="i" class="mb-2">
                <hr v-if="i > 0" class="mt-0 mb-1">
                <div v-for="(permission, j) in section" :key="j" class="d-flex pt-1">
                    <div class="pt-1">
                        {{ permission.value.alt }}
                    </div>
                    <div class="ms-auto">
                        <div class="btn-group btn-group-sm border border-secondary rounded overflow-hidden">
                            <label class="btn mb-0" :class="[checked(permission.key, permissionsState.Deny) ? 'btn-secondary' : 'btn-light']" @click="check(permission.key, permissionsState.Deny)">
                                <i class="fas fa-fw fa-times" :title="$t('[[[Zabierz dostęp]]]')" />
                            </label>
                            <label class="btn mb-0" :class="[checked(permission.key, null) ? 'btn-secondary' : 'btn-light']" @click="check(permission.key, null)">
                                <i class="fas fa-fw fa-minus" :title="$t('[[[Dostęp niezdefiniowany]]]')" />
                            </label>
                            <label class="btn mb-0" :class="[checked(permission.key, permissionsState.Allow) ? 'btn-secondary' : 'btn-light']" @click="check(permission.key, permissionsState.Allow)">
                                <i class="fas fa-fw fa-check" :title="$t('[[[Przyznaj dostęp]]]')" />
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <template #modal-footer="{ ok, cancel }">
                <IdeoButton variant="light" @click="cancel()">
                    <i class="fa fa-ban" />
                    {{ $t('[[[Anuluj]]]') }}
                </IdeoButton>
                <IdeoButton variant="success" :disabled="!form.active()" @click="savePermissions(); ok();">
                    <i class="fas fa-check" />
                    {{ $t('[[[Zapisz]]]') }}
                </IdeoButton>
            </template>
        </IdeoModal>
    </div>
</template>
